import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import style from './style.module.css';

import { 
    getLanguageShortname, Language, 
    getLanguageArray, systemLanguage, getLabel,
    reportsLabel, getUuid, getToken,
    Organization, getActiveOrganization, getOrganizations,
    activeOrganization, UserSimReport, shortname, urlUser
} from '../../utils/constants';

import { GetSimReports, GetAcademyReports, GetUserSimReports, GetReportExport } from '../../services/reportApi';

import Loader from '../../assets/images/loader.svg';

import Card from '../../components/Card/Card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faClose } from '@fortawesome/free-solid-svg-icons';

const Reports: React.FC = () => {

    const uuid = getUuid()
    const token = getToken()

    const [sessionLang, setSessionLang] = useState<Language>(getLanguageArray());

    const [userLanguage, setUserLanguage] = useState<{ short: string | any } | false>(false);

    const [userList,setUserList] = useState<[]>([])

    const user_language = getLanguageShortname();

    const [activeOrganizationId,setActiveOrganizationId] = useState<number | undefined>(getActiveOrganization()?.id)
    const [activeOrganizationName,setActiveOrganizationName] = useState<string | undefined>(undefined)

    const [filtersOn, setFiltersOn] = useState(false)

    const [simReports, setSimReports] = useState([])
    const [days,setDays] = useState(360)
    const [dateFrom, setDateFrom] = useState<string | "">("");
    const [dateTo, setDateTo] = useState<string | "">("");

    const [clickRate,setClickRate] = useState(undefined)
    const [clicked,setClicked] = useState(undefined)
    const [failureRate,setFailureRate] = useState(undefined)
    const [ignoreRate,setIgnoreRate] = useState(undefined)
    const [ignored,setIgnored] = useState(undefined)
    const [reported,setReported] = useState(undefined)
    const [sent, setSent] = useState(undefined)
    const [averageTime,setAverageTime] = useState<JSX.Element[]>([]);

    const [chaptersFinished,setChaptersFinished] = useState(undefined)
    const [quizzesTaken,setQuizzesTaken] = useState(undefined)
    const [correct,setCorrect] = useState(undefined)
    const [incorrect,setIncorrect] = useState(undefined)
    const [correctRate,setCorrectRate] = useState(undefined)
    const [incorrectRate,setIncorrectRate] = useState(undefined)
    const [quizzesTakenRate,setQuizzesTakenRate] = useState(undefined)

    const [reportLoading,setReportLoading] = useState(true)

    const handleResetFilters = () => {
        setDays(360)
        setDateFrom("")
        setDateTo("")
        handleActiveOrganizationChange()
    }

    const handleActiveOrganizationChange = async () => {
        setReportLoading(true)
        const activeOrg = getActiveOrganization(); 
        const organizationsList: Organization[] = getOrganizations();
        const activeId = activeOrg?.id;
        const idToFind = typeof activeId === 'number' ? activeId : (organizationsList[0]?.id);
        if (typeof idToFind !== 'number') {
            setActiveOrganizationName(undefined);
            return;
        }
        const selectedOrg = organizationsList.find((org: Organization) => org.id === idToFind);
        setActiveOrganizationId(selectedOrg?.id)
        setActiveOrganizationName(selectedOrg?.name ?? undefined);
        getSimReports(selectedOrg?.id)
    }

    const renderTimeComponent = (label: string, time: string) => {
        return (
            <div className={style.timeComponent} key={label}>
                <div className={style.timeComponentTimeLabel}>{label}</div>
                <div className={style.timeComponentTime}>{time}</div>
            </div>
        );
    };

    const handleFilters = () => {
        filtersOn ? setFiltersOn(false) : setFiltersOn(true)
    }

    const handleExport = async () => {

        const reportName = `phishnoo-report-${(getActiveOrganization()?.shortname)?.toLowerCase()}.pdf`

        await GetReportExport({
            uuid: uuid,
            token: token,
            organization_id: activeOrganizationId,
            days: days,
            date_from: dateFrom,
            date_to: dateTo,
            report_name: reportName
        })
    }

    const getSimReports = async (organizationId: number | undefined) => {
         const sims = await GetSimReports({
            uuid: uuid,
            token: token,
            organization_id: organizationId,
            days: days,
            date_from: dateFrom,
            date_to: dateTo
        });

        const academy = await GetAcademyReports({
            uuid: uuid,
            token: token,
            organization_id: organizationId,
            days: days,
            date_from: dateFrom,
            date_to: dateTo
        });

        const user = await GetUserSimReports({
            uuid: uuid,
            token: token,
            organization_id: organizationId,
            days: days,
            date_from: dateFrom,
            date_to: dateTo
        });

        const totalSeconds = sims.average_reporting_time;

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const avgDays = Math.floor(hours / 24);
        const remainingHours = hours % 24;

        const timeComponents = [
            renderTimeComponent('Minutes', String(minutes).padStart(2, "0")),
            renderTimeComponent('Seconds', String(seconds).padStart(2, "0")),
        ];
        if (avgDays > 0) {
            setAverageTime([
                renderTimeComponent('Days', String(avgDays)),
                renderTimeComponent('Hours', String(remainingHours).padStart(2, "0")),
                ...timeComponents,
            ]);
        } else {
            setAverageTime([
                renderTimeComponent('Hours', String(hours).padStart(2, "0")),
                ...timeComponents,
            ]);
        }

        setClickRate(sims.click_rate)
        setClicked(sims.clicked)
        setFailureRate(sims.failure_rate)
        setIgnoreRate(sims.ignore_rate)
        setIgnored(sims.ignored)
        setReported(sims.reported)
        setSent(sims.sent)

        setChaptersFinished(academy.chapters_finished)
        setQuizzesTaken(academy.quizzes_taken)
        setCorrect(academy.correct)
        setIncorrect(academy.incorrect)
        setCorrectRate(academy.quiz_correct_rate)
        setIncorrectRate(academy.quiz_incorrect_rate)
        setQuizzesTakenRate(academy.quiz_taken_rate)

        setUserList(user.report)

        setReportLoading(false)
    }

    useEffect(() => {
        if(simReports.length === 0)
        {
           handleActiveOrganizationChange()
        }
    }, [simReports])

    useEffect(() => {
        if (user_language) {
            setUserLanguage({ short: user_language.toUpperCase() });
        } else {
            setUserLanguage({ short: ((user_language as unknown) as Language).short || 'EN' });
        }
    }, [user_language]);

    useEffect(() => {
        window.scrollTo(0,0);
    }, []);

    useEffect(() => {
        const handler = (e: StorageEvent) => {
            if (e.key === systemLanguage && e.newValue) {
                setSessionLang(JSON.parse(e.newValue));
            }
        };
        window.addEventListener("storage", handler);
        return () => window.removeEventListener("storage", handler);
    }, []);

    useEffect(() => {
        const handler = (e: StorageEvent) => {
            if (e.key === activeOrganization && e.newValue) {
                handleActiveOrganizationChange()
            }
        };
        window.addEventListener("storage", handler);
        return () => window.removeEventListener("storage", handler);
    }, []);

    return (
        <div className={style.scoreboard}>
            <div className={style.header}>
                <div className={`${style.greeting} ${style.textContainerGrad}`}>
                    {getLabel(reportsLabel, sessionLang)}
                </div>
                <div className={style.info}>
                    {activeOrganizationName}
                </div>
            </div>
            <div className={style.report}>
                {
                    !reportLoading ?
                        <>
                        <div className={style.filters}>
                            {
                                filtersOn ?
                                    <>
                                    <div className={style.timeFilters}>
                                        <div className={`${style.filter} ${style.filterToggle}`} onClick={handleFilters}>
                                            <FontAwesomeIcon icon={faClose} />
                                            <span></span>
                                        </div>
                                        <div className={style.filter}>
                                            <input value={days} className={style.inputDays} onChange={(e) => setDays(Number(e.target.value))} /> <span>Days</span>
                                        </div>
                                        <div className={style.filter}>
                                            <input
                                                className={style.inputDate}
                                                type="date"
                                                value={dateFrom}
                                                onChange={(e) => {
                                                    const newFrom = e.target.value;
                                                    setDateFrom(newFrom);
                                                    if (dateTo && newFrom > dateTo) {
                                                            setDateTo("");
                                                    }
                                                }}
                                                max={dateTo || undefined}
                                            />
                                            <input
                                                type="date"
                                                className={style.inputDate}
                                                value={dateTo}
                                                onChange={(e) => setDateTo(e.target.value)}
                                                min={dateFrom || undefined}
                                            />

                                        <div className={`${style.filter} ${style.button} ${style.reset}`} onClick={handleResetFilters}>
                                            Reset
                                        </div>
                                        </div>
                                        <div className={`${style.filter} ${style.button}`} onClick={handleActiveOrganizationChange}>
                                            Update
                                        </div>
                                        <div className={`${style.filter} ${style.button} ${style.export}`} onClick={handleExport}>
                                            Export to PDF
                                        </div>
                                    </div>
                                    </> :
                                    <>
                                        <div className={`${style.filter} ${style.filterToggle}`} onClick={handleFilters}>
                                            <FontAwesomeIcon icon={faFilter} /><span>Filters</span>
                                        </div>
                                        <div className={`${style.filter} ${style.button}`} onClick={handleExport}>
                                            Export to PDF
                                        </div>
                                    </>
                                }
                        </div>
                        <div className={`${style.reportHeaders} ${style.smaller}`}>
                            Simulations
                        </div>
                        <div className={style.averageTime}>
                            <div className={`${style.averageTimeTitle}`}>
                                Average Reporting Time:
                            </div> 
                            {averageTime}
                        </div>
                        <div className={style.two}>
                            <Card
                                type="usage"
                                usageData={{
                                    title: "Clicked",
                                    color: "blue",
                                    title2: "",
                                    object: `${clicked}`,
                                    details: `Click Rate: ` + clickRate + '%',
                                    details2: "",
                                    percentage: clickRate ?? 0
                                }}
                            />
                        </div>
                        <div className={style.two}>
                            <Card
                                type="usage"
                                usageData={{
                                    title: "Reported",
                                    color: "green",
                                    title2: "",
                                    object: `${reported}`,
                                    details: Number.isNaN(Math.round((Number(reported ?? 0) / Number(sent ?? 1)) * 100)) ? "Reported Rate: 0%" : `Reported Rate: ` + Math.round((Number(reported ?? 0) / Number(sent ?? 1)) * 100) + '%',
                                    details2: "",
                                    percentage: Number.isNaN(Math.round((Number(reported ?? 0) / Number(sent ?? 1)) * 100)) ? 0 : Math.round((Number(reported ?? 0) / Number(sent ?? 1)) * 100)
                                }}
                            />
                        </div>
                        <div className={style.two}>
                            <Card
                                type="usage"
                                usageData={{
                                    title: "Ignored",
                                    title2: "",
                                    object: `${ignored}`,
                                    details: `Ignored Rate: ` + ignoreRate + '%',
                                    details2: "",
                                    percentage: ignoreRate ?? 0
                                }}
                            />
                        </div>
                        <div className={style.two}>
                            <Card
                                type="usage"
                                usageData={{
                                    title: "Failed",
                                    color:"red",
                                    title2: "",
                                    object: `${Math.round((ignored ?? 0) + (clicked ?? 0))}`,
                                    details: `Failure Rate: ` + failureRate + '%',
                                    details2: "",
                                    percentage: failureRate ?? 0
                                }}
                                chart="red"
                            />
                        </div>
                        
                        <div className={`${style.reportHeaders} ${style.smaller}`}>
                            Academy
                        </div>
                        <div className={style.two}>
                            <Card
                                type="usage"
                                usageData={{
                                    title: "Correct Answers",
                                    color: "green",
                                    title2: "",
                                    object: `${correct}`,
                                    details: `Correct Answers Rate: ` + correctRate + '%',
                                    details2: "",
                                    percentage: Number(correctRate)
                                }}
                            />
                        </div>
                        <div className={style.two}>
                            <Card
                                type="usage"
                                usageData={{
                                    title: "Incorrect Answers",
                                    color: "red",
                                    title2: "",
                                    object: `${incorrect}`,
                                    details: `Incorrect Answers Rate: ` + incorrectRate + '%',
                                    details2: "",
                                    percentage: Number(incorrectRate)
                                }}
                                chart="red"
                            />
                        </div>
                        <div className={style.two}>
                            <Card
                                type="usage"
                                usageData={{
                                    title: "Chapters Finished",
                                    color: "blue",
                                    title2: "",
                                    object: `${chaptersFinished}`,
                                    details: `Finished Rate: ` + '0' + '%',
                                    details2: "",
                                    percentage: 0
                                }}
                            />
                        </div>
                        <div className={style.two}>
                            <Card
                                type="usage"
                                usageData={{
                                    title: "Quizzes Taken",
                                    color: "blue",
                                    title2: "",
                                    object: `${quizzesTaken}`,
                                    details: `Quizzes Taken Rate: ` + quizzesTakenRate + '%',
                                    details2: "",
                                    percentage: Number(quizzesTakenRate)
                                }}
                            />
                        </div>
                        
                        <div className={`${style.reportHeaders} ${style.smaller}`}>
                            User Summary
                        </div>
                        <div className={style.tableWrap}>
                            <div className={`${style.tableRow} ${style.header}`}>
                                <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.email}`}>
                                    Name
                                </div><div className={`${style.tableCol} ${style.level}`}>
                                    Reported 
                                </div><div className={`${style.tableCol} ${style.level}`}>
                                    Clicked
                                </div><div className={`${style.tableCol} ${style.level}`}>
                                    Ignored
                                </div><div className={`${style.tableCol} ${style.level}`}>
                                    Chapters Completed
                                </div><div className={`${style.tableCol} ${style.level}`}>
                                    Quizes Taken
                                </div><div className={`${style.tableCol} ${style.level}`}>
                                    Correct
                                </div><div className={`${style.tableCol} ${style.level}`}>
                                    Incorrect
                                </div><div className={`${style.tableCol} ${style.shortname} ${style.actions}`}></div>
                            </div>
                            {userList.length > 0 && userList?.map((user: UserSimReport) => (
                                <div className={style.tableRow} key={user.uuid}>
                                    <div className={`${style.tableCol} ${style.shortname}`}>
                                        <span>{shortname(user.firstname + ' ' + user.lastname)}</span>    
                                    </div><div className={`${style.tableCol} ${style.email}`}>
                                        <Link to={urlUser + '/' + user.uuid}>{user.firstname} {user.lastname}</Link>
                                    </div><div className={`${style.tableCol} ${style.level}`}>
                                        {user.reported_count}
                                    </div><div className={`${style.tableCol} ${style.level}`}>
                                        {user.clicked_count}
                                    </div><div className={`${style.tableCol} ${style.level}`}>
                                        {user.ignored_count}
                                    </div><div className={`${style.tableCol} ${style.level}`}>
                                        {user.academy_chapters_completed}
                                    </div><div className={`${style.tableCol} ${style.level}`}>
                                        {user.quizes_taken}
                                    </div><div className={`${style.tableCol} ${style.level}`}>
                                        {user.questions_correct}
                                    </div><div className={`${style.tableCol} ${style.level}`}>
                                        {user.questions_incorrect}
                                    </div><div className={`${style.tableCol} ${style.shortname} ${style.actions}`}></div>
                                </div>
                            ))}
                         </div>
                        </>
                    :
                    <div className={style.loading}>
                        <img src={Loader} />
                    </div>
                }
            </div>

        </div>
    );
}

export default Reports;
